import { useActions } from '@tock/shared/utils/connect';

type PhoneArgs = { phone: string; phoneCountryCode: string };

export type WithPhoneValidationProps = {
  validatePhone: (args: PhoneArgs) => Promise<true>;
};

/**
 * A little temporary wrapper to share phone validation used in submit handlers.
 * Rejects with an error messages, resolves with true when successful
 */
export const usePhoneValidation = () => {
  const { actions } = useActions();

  const validatePhone = async ({ phone, phoneCountryCode }: PhoneArgs) => {
    try {
      const { payload } = await actions.patron.validatePhoneRequest({ phone, phoneCountryCode });
      if (!payload.isValid) {
        return Promise.reject('Please provide a valid phone number and submit again');
      }
      return payload.isValid;
    } catch {
      return Promise.reject('Failed to validate phone number, please try again');
    }
  };

  return validatePhone;
};

/**
 * HOC wrapper to utilize the phone validation util in a class component
 */
export const withPhoneValidation = <P,>(
  Component: React.ComponentType<React.PropsWithChildren<P & WithPhoneValidationProps>>
) => {
  const WithPhoneValidation = (props: P) => {
    const validatePhone = usePhoneValidation();
    // The any comes from a bad interaction between emotion and react types.
    // See: https://github.com/emotion-js/emotion/issues/2169
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    return <Component validatePhone={validatePhone} {...(props as any)} />;
  };

  const componentName = Component.displayName || Component.name || 'Component';
  WithPhoneValidation.displayName = `WithPhoneValidation(${componentName})`;

  return WithPhoneValidation;
};
